import { useEffect, useState } from 'react';
import CardView from './CardView';
import { isOwnerOf } from './lib/device';
import { HexMark } from './components/icons';
import './card.css';
import './themes';

function readInlineData() {
    const el = document.getElementById('hx-card-data');
    if (!el) return null;
    try {
        return JSON.parse(el.textContent);
    } catch {
        return null;
    }
}

function uuidFromPath(pathname = window.location.pathname) {
    return pathname.match(/\/card\/([a-fA-F0-9-]+)/)?.[1] || null;
}

export function CardSkeleton() {
    return (
        <div className="hx-card" data-theme="default" aria-busy="true" aria-label="Loading card">
            <main className="hx-shell" style={{ paddingTop: 'calc(30px + env(safe-area-inset-top))' }}>
                <div className="hx-skel" style={{ width: 124, height: 124, borderRadius: '50%', margin: '0 auto 18px' }} />
                <div className="hx-skel" style={{ width: '62%', height: 34, margin: '0 auto 12px' }} />
                <div className="hx-skel" style={{ width: '44%', height: 16, margin: '0 auto 26px' }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
                    {[0, 1, 2, 3].map((k) => (
                        <div key={k} className="hx-skel" style={{ height: 68 }} />
                    ))}
                </div>
                <div className="hx-skel" style={{ height: 140, borderRadius: 22 }} />
            </main>
        </div>
    );
}

export function CardNotFound() {
    return (
        <div className="hx-card" data-theme="default">
            <div className="hx-empty">
                <div>
                    <div className="hx-chip" style={{ width: 64, height: 64, margin: '0 auto 18px', borderRadius: 20 }}>
                        <HexMark style={{ width: 30, height: 30 }} />
                    </div>
                    <h1 className="hx-promo-title" style={{ fontSize: 28 }}>
                        This card isn&apos;t active
                    </h1>
                    <p className="hx-promo-text">The link may be mistyped, or the owner turned the card off.</p>
                    <a className="hx-btn hx-btn-primary" href="https://nordic3d.us/hexlink.html?utm_source=hexlink&utm_medium=card&utm_campaign=not_found">
                        Get your own HexLink
                    </a>
                </div>
            </div>
        </div>
    );
}

export default function CardPage({ uuid: uuidProp }) {
    const uuid = uuidProp || uuidFromPath();
    const [state, setState] = useState(() => {
        const inline = readInlineData();
        if (inline?.card && (!uuid || inline.card.uuid === uuid)) return { card: inline.card };
        if (inline?.notFound) return { notFound: true };
        return { loading: true };
    });

    useEffect(() => {
        if (!state.loading || !uuid) {
            if (!uuid) setState({ notFound: true });
            return undefined;
        }
        let cancelled = false;
        fetch(`/api/card/${uuid}`, { headers: { Accept: 'application/json' } })
            .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (cancelled) return;
                setState(ok && d?.card ? { card: d.card } : { notFound: true });
            })
            .catch(() => !cancelled && setState({ notFound: true }));
        return () => {
            cancelled = true;
        };
    }, [state.loading, uuid]);

    if (state.loading) return <CardSkeleton />;
    if (state.notFound || !state.card) return <CardNotFound />;
    return <CardView card={state.card} isOwner={isOwnerOf(state.card.uuid)} />;
}
